import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  monthKeyToParts,
  mortgageInterestByMonth,
  normalizeFromSettings,
} from "@/components/properties/cashflow-metrics";
import { useAuth } from "@/hooks/use-auth";
import { useOwnedProperties } from "@/hooks/use-owned-properties";
import { usePropertyLogs } from "@/hooks/use-property-logs";
import { useWorkspace } from "@/hooks/use-workspace";
import { round2 } from "@/lib/calculators";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";
import { toast } from "sonner";

export interface ManualTransaction {
  id: string;
  workspace_id: string;
  pipeline_property_id: string | null;
  created_by_user_id: string;
  transaction_date: string;
  amount: number;
  type: string;
  category: string;
  description: string | null;
  vendor: string | null;
  is_tax_deductible: boolean;
  notes: string | null;
  created_at: string;
}

export interface AggregatedTransaction {
  date: string;
  amount: number;
  type: "income" | "expense";
  category: string;
  description: string;
  source: string; // which module it came from
  source_label?: string; // human-readable origin shown on auto rows
  property_label?: string;
  pipeline_property_id?: string;
  manual_id?: string; // set only for manual entries, enables edit/delete
}

// Schedule E line item categories. `source` mirrors where the auto figure
// originates so the Schedule E view can attribute each line.
export const SCHEDULE_E_CATEGORIES = [
  { line: 3, key: "rents_received", label: "Rents received", type: "income", source: "Rent payments" },
  { line: 5, key: "advertising", label: "Advertising", type: "expense", source: "Manual entries" },
  { line: 6, key: "auto_travel", label: "Auto and travel", type: "expense", source: "Manual entries" },
  { line: 7, key: "cleaning_maintenance", label: "Cleaning and maintenance", type: "expense", source: "Work orders" },
  { line: 8, key: "commissions", label: "Commissions", type: "expense", source: "Manual entries" },
  { line: 9, key: "insurance", label: "Insurance", type: "expense", source: "Manual entries" },
  { line: 10, key: "legal_professional", label: "Legal and professional fees", type: "expense", source: "Manual entries" },
  { line: 11, key: "management_fees", label: "Management fees", type: "expense", source: "Manual entries" },
  { line: 12, key: "mortgage_interest", label: "Mortgage interest paid", type: "expense", source: "Financing · amortization" },
  { line: 13, key: "other_interest", label: "Other interest", type: "expense", source: "Manual entries" },
  { line: 14, key: "repairs", label: "Repairs", type: "expense", source: "Rehab · completed tasks" },
  { line: 15, key: "supplies", label: "Supplies", type: "expense", source: "Manual entries" },
  { line: 16, key: "taxes", label: "Taxes", type: "expense", source: "Manual entries" },
  { line: 17, key: "utilities", label: "Utilities", type: "expense", source: "Manual entries" },
  { line: 18, key: "depreciation", label: "Depreciation", type: "expense", source: "Computed · 27.5-yr straight line" },
  { line: 19, key: "other", label: "Other expenses", type: "expense", source: "Manual entries" },
] as const;

// Tax advantages knowledge base — grouped reference shown on the Overview tab.
export type TaxAdvantageTone = "green" | "blue" | "amber" | "red";

export interface TaxAdvantageItem {
  name: string;
  tag: string;
  tone: TaxAdvantageTone;
  tagIcon?: "warning" | "sparkle";
  desc: string;
}

export interface TaxAdvantageGroup {
  key: string;
  title: string;
  subtitle: string;
  tone: Exclude<TaxAdvantageTone, "red">;
  icon: "check" | "lightbulb" | "warning" | "tree";
  items: TaxAdvantageItem[];
}

export const TAX_ADVANTAGE_GROUPS: TaxAdvantageGroup[] = [
  {
    key: "automatic",
    title: "Automatic · No action needed",
    subtitle: "Deductions that apply by default to every rental owner",
    tone: "green",
    icon: "check",
    items: [
      {
        name: "Depreciation",
        tag: "Automatic",
        tone: "green",
        desc: "Deduct 1/27.5 of your residential property's improvement basis every year (1/39 for commercial). A non-cash deduction that often turns a positive-cash-flow rental into a paper loss.",
      },
      {
        name: "Mortgage interest",
        tag: "Automatic",
        tone: "green",
        desc: "Interest on loans secured by a rental is fully deductible against rental income — no $750k cap like a primary residence. Includes points amortized over loan life.",
      },
    ],
  },
  {
    key: "strategies",
    title: "Strategies · Require planning",
    subtitle: "Active moves that can unlock significant savings with proper structuring",
    tone: "blue",
    icon: "lightbulb",
    items: [
      {
        name: "1031 exchange",
        tag: "Defer gains",
        tone: "blue",
        desc: 'Defer capital gains indefinitely by rolling sale proceeds into a "like-kind" replacement property within 45/180 days. Stack them lifelong and reset basis with a step-up at death.',
      },
      {
        name: "QBI deduction (§199A)",
        tag: "Up to 20%",
        tone: "blue",
        desc: 'Rental real estate that rises to a "trade or business" can deduct up to 20% of qualified business income. The Safe Harbor (Rev. Proc. 2019-38) gives clear rules — 250+ hours of rental services, separate books, contemporaneous records.',
      },
      {
        name: "Cost segregation",
        tag: "Accelerated",
        tone: "blue",
        desc: "A cost-seg study reclassifies 20–30% of your purchase price as 5/7/15-yr property — letting you front-load depreciation in the first few years instead of spreading it over 27.5.",
      },
      {
        name: "Bonus depreciation",
        tag: "Phasing out · 20% in 2026",
        tone: "amber",
        tagIcon: "warning",
        desc: "First-year write-off on qualifying improvement assets (appliances, landscaping, certain rehab work). 60% in 2024, 40% in 2025, 20% in 2026 — unless extended.",
      },
      {
        name: "Real estate pro status",
        tag: "Game changer",
        tone: "blue",
        tagIcon: "sparkle",
        desc: "750+ hours/yr in real estate trades + more than half your work time = REPS. Unlocks unlimited deduction of rental losses against ordinary income (W-2, business).",
      },
    ],
  },
  {
    key: "watch",
    title: "Rules to watch · Traps & limitations",
    subtitle: "Provisions that can blunt or recharacterize your deductions if you're not careful",
    tone: "amber",
    icon: "warning",
    items: [
      {
        name: "Passive loss rules",
        tag: "Watch out",
        tone: "amber",
        desc: "Rentals are passive by default — losses can only offset passive income unless your AGI is under $150k (up to $25k allowance) or you qualify as a Real Estate Professional.",
      },
      {
        name: "Self-rental rules",
        tag: "Trap",
        tone: "red",
        desc: 'Renting to your own business "recharacterizes" the income as non-passive, so it can\'t absorb other passive losses. Common surprise for owners with operating businesses.',
      },
    ],
  },
  {
    key: "longgame",
    title: "Long-game · Estate planning",
    subtitle: "Provisions that compound across a lifetime and into the next generation",
    tone: "blue",
    icon: "tree",
    items: [
      {
        name: "Step-up in basis",
        tag: "Estate",
        tone: "blue",
        desc: 'Heirs inherit your property at fair market value — wiping out a lifetime of deferred gains. Combined with 1031 exchanges, the "buy, refi, die" strategy is fully legal.',
      },
    ],
  },
];

// Map system data to Schedule E categories
function mapToScheduleE(source: string, category?: string): string {
  const mapping: Record<string, string> = {
    rent_payment: "rents_received",
    loan_interest: "mortgage_interest",
    loan_points: "mortgage_interest",
    rehab_item: "repairs",
    work_order: "cleaning_maintenance",
    property_tax: "taxes",
    insurance: "insurance",
  };
  return mapping[source] || category || "other";
}

export function useAccounting(year?: number) {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const qc = useQueryClient();
  const workspaceId = currentWorkspace?.id;
  const taxYear = year || new Date().getFullYear();

  const startDate = `${taxYear}-01-01`;
  const endDate = `${taxYear}-12-31`;

  // Fetch rent payments
  const rentPaymentsQuery = useQuery({
    queryKey: ["accounting-rents", workspaceId, taxYear],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("rent_payments")
        .select("*")
        .eq("workspace_id", workspaceId)
        .eq("status", "paid")
        .gte("paid_date", startDate)
        .lte("paid_date", endDate);
      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId,
  });

  // Fetch rehab costs — only genuinely completed line items (status "completed"
  // with a completion date) count as a repair expense, dated by `completed_at`
  // and scoped to the tax year. Mirrors the Properties cash-flow model, which
  // attributes rehab spend by each item's completion month.
  const rehabQuery = useQuery({
    queryKey: ["accounting-rehab", workspaceId, taxYear],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("rehab_line_items")
        .select("*, rehab_projects(pipeline_property_id)")
        .eq("workspace_id", workspaceId)
        .eq("status", "completed")
        .not("actual_cost", "is", null)
        .not("completed_at", "is", null)
        .gte("completed_at", startDate)
        .lte("completed_at", endDate + "T23:59:59Z");
      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId,
  });

  // Fetch work order costs
  const workOrdersQuery = useQuery({
    queryKey: ["accounting-workorders", workspaceId, taxYear],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("work_orders")
        .select("*")
        .eq("workspace_id", workspaceId)
        .eq("status", "completed")
        .not("cost_actual", "is", null)
        .gte("completed_at", startDate)
        .lte("completed_at", endDate + "T23:59:59Z");
      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId,
  });

  // Fetch manual transactions
  const manualQuery = useQuery({
    queryKey: ["manual-transactions", workspaceId, taxYear],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("manual_transactions")
        .select("*")
        .eq("workspace_id", workspaceId)
        .gte("transaction_date", startDate)
        .lte("transaction_date", endDate)
        .order("transaction_date", { ascending: false });
      if (error) throw error;
      return data as unknown as ManualTransaction[];
    },
    enabled: !!workspaceId,
  });

  // Owned properties (is_owned = true) drive depreciation and mortgage interest.
  // We reuse the same source of truth as the Properties section: `owned_date`
  // for acquisition timing, the composed `property_setting` deal inputs, and the
  // `loans` switcher row (loan_type + is_enabled). Past-month loan terms come
  // from the frozen `property_log` snapshots.
  const { items: ownedItems, isLoading: ownedIsLoading } = useOwnedProperties();
  const { logsByProperty, isLoading: logsIsLoading } = usePropertyLogs();

  // Owned properties, exposed for per-property tabs/filters.
  const properties = useMemo(
    () =>
      ownedItems.map((p) => ({
        id: p.id,
        label: p.property.address ?? "Property",
      })),
    [ownedItems],
  );

  // Aggregate all transactions
  const allTransactions = useMemo<AggregatedTransaction[]>(() => {
    const txns: AggregatedTransaction[] = [];
    const propLabel = (id?: string | null) =>
      (id && properties.find((p) => p.id === id)?.label) || undefined;

    // Rent payments → income
    (rentPaymentsQuery.data || []).forEach((p) => {
      txns.push({
        date: p.paid_date!,
        amount: Number(p.amount),
        type: "income",
        category: "rents_received",
        description: `Rent from ${p.tenant_name}`,
        source: "Rent Payments",
        source_label: "Rent payments",
        property_label: propLabel(p.pipeline_property_id),
        pipeline_property_id: p.pipeline_property_id,
      });
      if (p.late_fee && Number(p.late_fee) > 0) {
        txns.push({
          date: p.paid_date!,
          amount: Number(p.late_fee),
          type: "income",
          category: "rents_received",
          description: `Late fee from ${p.tenant_name}`,
          source: "Rent Payments",
          source_label: "Rent payments",
          property_label: propLabel(p.pipeline_property_id),
          pipeline_property_id: p.pipeline_property_id,
        });
      }
    });

    // Mortgage interest — resolved per owned property, per month, the same way
    // the Properties cash-flow does: the current month is live from
    // `property_setting` + the loans switch, past months from frozen
    // `property_log` snapshots. Only the deductible interest portion is recorded
    // (Schedule E line 12 is interest, not principal), and only for months the
    // loan was actually active within the property's ownership window.
    ownedItems.forEach((item) => {
      const strategy = item.strategy ?? "BRRRR";
      const live = normalizeFromSettings({
        strategy,
        settings: item.settings,
        loan: item.loan,
        rentAmount: item.actualMonthlyRent,
      });
      const interestByMonth = mortgageInterestByMonth({
        year: taxYear,
        strategy,
        logs: logsByProperty.get(item.id) ?? [],
        live,
        acquisitionDate: item.ownedDate,
        saleDate: item.closedDate,
      });
      const addr = item.property.address ?? "Property";
      interestByMonth.forEach((interest, key) => {
        const { year, month } = monthKeyToParts(key);
        txns.push({
          date: `${year}-${String(month).padStart(2, "0")}-01`,
          amount: round2(interest),
          type: "expense",
          category: "mortgage_interest",
          description: `Mortgage interest - ${addr}`,
          source: "Loans",
          source_label: "Financing · amortization",
          property_label: propLabel(item.id),
          pipeline_property_id: item.id,
        });
      });
    });

    // Rehab costs → repairs. Dated by `completed_at` (the completion date), with
    // the property taken from the row's own `pipeline_property_id`, falling back
    // to its rehab project's.
    (rehabQuery.data || []).forEach((item) => {
      const rehabPropId =
        (item as { pipeline_property_id?: string | null }).pipeline_property_id ??
        (item as { rehab_projects?: { pipeline_property_id?: string } }).rehab_projects
          ?.pipeline_property_id ??
        undefined;
      txns.push({
        date:
          (item as { completed_at?: string | null }).completed_at?.split("T")[0] ||
          item.updated_at?.split("T")[0] ||
          item.created_at.split("T")[0],
        amount: Number(item.actual_cost),
        type: "expense",
        category: "repairs",
        description: `${item.category}: ${item.description || "Rehab work"}`,
        source: "Rehab",
        source_label: "Rehab · completed tasks",
        property_label: propLabel(rehabPropId),
        pipeline_property_id: rehabPropId,
      });
    });

    // Work orders → maintenance
    (workOrdersQuery.data || []).forEach((wo) => {
      txns.push({
        date: wo.completed_at?.split("T")[0] || wo.created_at.split("T")[0],
        amount: Number(wo.cost_actual),
        type: "expense",
        category: "cleaning_maintenance",
        description: `${wo.title}${wo.vendor_name ? ` (${wo.vendor_name})` : ""}`,
        source: "Work Orders",
        source_label: "Work orders",
        property_label: propLabel(wo.pipeline_property_id),
        pipeline_property_id: wo.pipeline_property_id,
      });
    });

    // Depreciation from owned properties — 27.5-yr straight line on 80% of the
    // purchase price. Prorated by months owned in the acquisition year (from
    // `owned_date`); no depreciation for tax years before the property was
    // owned; a full year thereafter.
    ownedItems.forEach((item) => {
      const price = Number(item.settings?.purchase_price ?? item.purchasePrice ?? 0);
      if (price <= 0) return;

      const ownedYear = Number(item.ownedDate?.slice(0, 4));
      if (Number.isFinite(ownedYear) && taxYear < ownedYear) return; // not yet owned
      const ownedMonth = Number(item.ownedDate?.slice(5, 7)) || 1;
      const monthsOwned =
        Number.isFinite(ownedYear) && taxYear === ownedYear ? 13 - ownedMonth : 12;

      const buildingValue = price * 0.8; // 80% building, 20% land
      const annualDepreciation = (buildingValue / 27.5) * (monthsOwned / 12);
      if (annualDepreciation <= 0) return;

      const addr = item.property.address ?? "Property";
      txns.push({
        date: `${taxYear}-12-31`,
        amount: round2(annualDepreciation),
        type: "expense",
        category: "depreciation",
        description: `Depreciation - ${addr}`,
        source: "Depreciation",
        source_label: "Computed · 27.5-yr straight line",
        property_label: propLabel(item.id),
        pipeline_property_id: item.id,
      });
    });

    // Manual transactions
    (manualQuery.data || []).forEach((mt) => {
      txns.push({
        date: mt.transaction_date,
        amount: Number(mt.amount),
        type: mt.type as "income" | "expense",
        category: mt.category,
        description: mt.description || mt.category,
        source: "Manual",
        source_label: "Manual entry",
        property_label: propLabel(mt.pipeline_property_id),
        pipeline_property_id: mt.pipeline_property_id || undefined,
        manual_id: mt.id,
      });
    });

    return txns.sort((a, b) => b.date.localeCompare(a.date));
  }, [rentPaymentsQuery.data, rehabQuery.data, workOrdersQuery.data, ownedItems, logsByProperty, properties, manualQuery.data, taxYear]);

  // Schedule E breakdown
  const scheduleE = useMemo(() => {
    const breakdown: Record<string, number> = {};
    SCHEDULE_E_CATEGORIES.forEach((cat) => {
      breakdown[cat.key] = 0;
    });
    allTransactions.forEach((txn) => {
      if (breakdown[txn.category] !== undefined) {
        breakdown[txn.category] += txn.amount;
      } else {
        breakdown.other += txn.amount;
      }
    });
    return breakdown;
  }, [allTransactions]);

  const totalIncome = useMemo(() => allTransactions.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0), [allTransactions]);
  const totalExpenses = useMemo(() => allTransactions.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0), [allTransactions]);

  const createManualTransaction = useMutation({
    mutationFn: async (data: Omit<ManualTransaction, "id" | "workspace_id" | "created_by_user_id" | "created_at">) => {
      if (!workspaceId || !user) throw new Error("No workspace");
      const { error } = await supabase.from("manual_transactions").insert({
        workspace_id: workspaceId,
        created_by_user_id: user.id,
        ...data,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Transaction added");
      qc.invalidateQueries({ queryKey: ["manual-transactions"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to create transaction")),
  });

  const updateManualTransaction = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<Omit<ManualTransaction, "id" | "workspace_id" | "created_by_user_id" | "created_at">>) => {
      const { data: rows, error } = await supabase
        .from("manual_transactions")
        .update(data as never)
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "update this transaction");
    },
    onSuccess: () => {
      toast.success("Transaction updated");
      qc.invalidateQueries({ queryKey: ["manual-transactions"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update transaction")),
  });

  const deleteManualTransaction = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.from("manual_transactions").delete().eq("id", id).select("id");
      if (error) throw error;
      assertRowsAffected(data, "delete this transaction");
    },
    onSuccess: () => {
      toast.success("Transaction deleted");
      qc.invalidateQueries({ queryKey: ["manual-transactions"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to delete transaction")),
  });

  const isLoading = rentPaymentsQuery.isLoading || rehabQuery.isLoading || workOrdersQuery.isLoading || manualQuery.isLoading || ownedIsLoading || logsIsLoading;

  return {
    allTransactions,
    manualTransactions: manualQuery.data || [],
    properties,
    scheduleE,
    totalIncome,
    totalExpenses,
    netIncome: totalIncome - totalExpenses,
    isLoading,
    createManualTransaction,
    updateManualTransaction,
    deleteManualTransaction,
    taxYear,
  };
}
